import React, { useState, useMemo, useEffect, useRef } from "react";
import { API_BASE } from "../api/config";
import Layout from "../components/Layout";
import { useTable, useGlobalFilter, useSortBy, usePagination } from "react-table";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";

function Products() {
  const [data, setData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, type: "add", row: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, row: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formBranches, setFormBranches] = useState([]); // selected branch IDs

  const inputRef = useRef(null);

  // Autofocus when modal opens
  useEffect(() => {
    if (modal.open && inputRef.current) inputRef.current.focus();
  }, [modal.open]);

  // Fetch branches
  const fetchBranches = async () => {
    try {
      const res = await fetch(
        `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=branches&condition=status`
      );
      const json = await res.json();
      setBranches(json || []);
    } catch (err) {
      console.error("Error fetching branches:", err);
    }
  };

  // Fetch products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_BASE}?key=dzital_key&pswd=Hibujji@123&transac=get&table=products`
      );
      const result = await response.json();

      const formattedData = result.map((item, index) => ({
        sno: item.id,
        srno: index + 1,
        name: item.name,
        status: item.status === "1" ? "Active" : "Inactive",
        raw_status: item.status,
        branches: item.branch || "", // comma separated string of branch IDs
      }));

      setData(formattedData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching products:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchBranches();
  }, []);

  const columns = useMemo(
    () => [
      { Header: "S.No", accessor: "srno" },
      { Header: "Name", accessor: "name" },
      {
        Header: "Branches",
        accessor: "branches"
      },
      { Header: "Status", accessor: "status" },
      {
        Header: "Actions",
        Cell: ({ row }) => (
          <div className="flex gap-2">
            <button
              className="px-2 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
              onClick={() => {
                const branchIds = row.original.branches
                  ? row.original.branches.split(",")
                  : [];
                setFormBranches(branchIds);
                setModal({ open: true, type: "edit", row: row.original });
              }}
            >
              Edit
            </button>
            <button
              className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
              onClick={() => setDeleteModal({ open: true, row: row.original })}
            >
              Delete
            </button>
          </div>
        ),
      },
    ],
    [branches]
  );

  const tableInstance = useTable(
    { columns, data, initialState: { pageSize: 5 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state,
    setGlobalFilter,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    gotoPage,
    state: { pageIndex },
  } = tableInstance;

  const { globalFilter } = state;

  // Submit handler
  const handleSubmit = async (e) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    e.preventDefault();

    const form = e.target;
    const name = form.name.value;
    const status = form.status.value;
    const branch = formBranches.join(",");

    const payload = { name, status, branch: branch };

    let transac = "insert";
    if (modal.type === "edit") {
      payload.id = modal.row.sno;
      transac = "update";
    }

    try {
      const response = await fetch(
        `${API_BASE}?key=dzital_key&pswd=Hibujji@123&table=products&transac=${transac}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const result = await response.json();
      if (result.status === (transac === "insert" ? "inserted" : "updated")) {
        fetchProducts();
        setModal({ open: false, type: "add", row: null });
        setFormBranches([]);
      } else {
        alert("Error: " + result.message);
      }
      setIsSubmitting(false);
    } catch (error) {
      console.error("Error submitting product:", error);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    const payload = {
      key: "dzital_key",
      pswd: "Hibujji@123",
      table: "products",
      id: deleteModal.row.sno,
      transac: "delete",
    };

    try {
      const response = await fetch(
        `${API_BASE}?key=dzital_key&pswd=Hibujji@123&table=products&transac=delete`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const result = await response.json();
      if (result.status === "deleted") {
        fetchProducts();
        setDeleteModal({ open: false, row: null });
      } else {
        alert("Error deleting product: " + result.message);
      }
    } catch (error) {
      console.error("Error deleting product:", error);
    }
  };

  // Export helpers
  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    XLSX.writeFile(wb, "products.xlsx");
  };
  const exportCSV = () => {
    const csvContent = [
      columns.map((col) => col.Header).join(","),
      ...data.map((row) => columns.map((col) => row[col.accessor] || "").join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "products.csv");
  };
  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    saveAs(blob, "products.json");
  };
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("Products Table", 20, 10);
    doc.autoTable({
      head: [columns.filter((c) => c.accessor).map((c) => c.Header)],
      body: data.map((row) =>
        columns.filter((c) => c.accessor).map((c) => row[c.accessor])
      ),
      startY: 20,
    });
    doc.save("products.pdf");
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center text-white bg-gradient-to-b from-blue-900 to-black">
          <p>Loading products...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-blue-900 to-black p-6 text-white">
        <h1 className="text-2xl font-bold mb-4">Products</h1>

        {/* Controls */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="text"
              placeholder="Search..."
              value={globalFilter || ""}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="p-2 border border-gray-300 rounded text-black"
            />
            <button onClick={exportExcel} className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">Excel</button>
            <button onClick={exportCSV} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">CSV</button>
            <button onClick={exportJSON} className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600">JSON</button>
            <button onClick={exportPDF} className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600">PDF</button>
          </div>

          <button
            onClick={() => {
              setFormBranches([]);
              setModal({ open: true, type: "add", row: null });
            }}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Add
          </button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white bg-opacity-10 rounded">
          <table {...getTableProps()} className="min-w-full border border-gray-300">
            <thead className="bg-gray-200 bg-opacity-20 text-white">
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      className="px-4 py-2 border cursor-pointer"
                    >
                      {column.render("Header")}
                      <span>{column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}</span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className="hover:bg-gray-100 hover:bg-opacity-20">
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()} className="px-4 py-2 border">
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-2 flex items-center gap-2">
          <button onClick={() => gotoPage(0)} disabled={!canPreviousPage} className="px-2 py-1 bg-gray-300 rounded">{"<<"}</button>
          <button onClick={previousPage} disabled={!canPreviousPage} className="px-2 py-1 bg-gray-300 rounded">Previous</button>
          <span>Page {pageIndex + 1} of {pageOptions.length}</span>
          <button onClick={nextPage} disabled={!canNextPage} className="px-2 py-1 bg-gray-300 rounded">Next</button>
          <button onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage} className="px-2 py-1 bg-gray-300 rounded">{">>"}</button>
        </div>

        {/* Add/Edit Modal */}
        {modal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
            <div className="bg-white bg-opacity-95 p-6 rounded shadow-md w-96 text-black">
              <h2 className="text-xl font-bold mb-4">
                {modal.type === "add" ? "Add Product" : "Edit Product"}
              </h2>
              <form onSubmit={handleSubmit} className="flex flex-col gap-2">
                <input
                  type="text"
                  name="name"
                  placeholder="Product Name"
                  defaultValue={modal.row?.name || ""}
                  required
                  className="p-2 border rounded"
                  ref={inputRef}
                />

                {/* Multi-branch selection */}
                <label className="font-semibold">Select Branches:</label>
                <select
                  multiple
                  value={formBranches}
                  onChange={(e) =>
                    setFormBranches(
                      Array.from(e.target.selectedOptions, (opt) => opt.value)
                    )
                  }
                  className="p-2 border rounded h-32"
                  required
                >
                  {branches.map((b) => (
                    <option key={b.id} value={b.name}>
                      {b.name}
                    </option>
                  ))}
                </select>

                <select
                  name="status"
                  defaultValue={modal.row?.raw_status ?? "1"}
                  className="p-2 border rounded"
                  required
                >
                  <option value="1">Active</option>
                  <option value="0">Inactive</option>
                </select>
                <div className="flex justify-end gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setModal({ open: false, type: "add", row: null });
                      setFormBranches([]);
                    }}
                    className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-4 py-2 text-white rounded ${
                      isSubmitting
                        ? "bg-blue-300 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {deleteModal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
            <div className="bg-white bg-opacity-95 p-6 rounded shadow-md w-96 text-black">
              <h2 className="text-xl font-bold mb-4 text-red-600">Confirm Delete</h2>
              <p>
                Are you sure you want to delete <strong>{deleteModal.row.name}</strong>?
              </p>
              <div className="flex justify-end gap-2 mt-4">
                <button
                  className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
                  onClick={() => setDeleteModal({ open: false, row: null })}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Products;
